import { defineComponent, h, useAttrs } from 'vue'
import { VBadge } from 'vuetify/components'
import { hasBooleanishAttr } from './legacyInputProps'

VBadge.compatConfig = {
  ...(VBadge.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: false
}

function normalizeVisibility(value) {
  if (value == null) return undefined
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  if (typeof value === 'string') {
    if (value === '') return true
    const normalized = value.trim().toLowerCase()
    return normalized !== '0' && normalized !== 'false'
  }
  return Boolean(value)
}

function normalizeContent(content) {
  if (content == null) return undefined
  return String(content)
}

export default defineComponent({
  name: 'VBadgeCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,

  setup(_, { slots }) {
    const attrs = useAttrs()

    return () => {
      const normalizedAttrs = { ...attrs }
      const modelValue =
        attrs.modelValue !== undefined ? attrs.modelValue : attrs.value
      const content = normalizeContent(attrs.content)
      const visible =
        modelValue !== undefined
          ? normalizeVisibility(modelValue)
          : normalizeVisibility(content)

      if (normalizedAttrs.offsetX == null && attrs['offset-x'] != null) {
        normalizedAttrs.offsetX = attrs['offset-x']
      }
      if (normalizedAttrs.offsetY == null && attrs['offset-y'] != null) {
        normalizedAttrs.offsetY = attrs['offset-y']
      }
      if (normalizedAttrs.textColor == null && attrs['text-color'] != null) {
        normalizedAttrs.textColor = attrs['text-color']
      }
      if (normalizedAttrs.location == null && hasBooleanishAttr(attrs.bottom)) {
        normalizedAttrs.location = hasBooleanishAttr(attrs.left)
          ? 'bottom start'
          : 'bottom end'
      } else if (
        normalizedAttrs.location == null &&
        hasBooleanishAttr(attrs.left)
      ) {
        normalizedAttrs.location = 'top start'
      }

      delete normalizedAttrs.value
      delete normalizedAttrs['offset-x']
      delete normalizedAttrs['offset-y']
      delete normalizedAttrs['text-color']
      delete normalizedAttrs.bottom
      delete normalizedAttrs.left
      delete normalizedAttrs.overlap

      if (visible === false) {
        return slots.default?.()
      }

      // Vuetify 2's <v-badge overlap> (no `inline`) with no wrapped default
      // slot still rendered as a normal-flow badge sitting next to whatever
      // markup follows it (e.g. TheSubHeader.vue's tab-count badges, used
      // with no wrapped icon/content at all). Vuetify 3's VBadge always
      // corner-positions the badge absolutely relative to its wrapper
      // (`overlap` isn't even a real V3 prop - we strip it above), and with
      // no wrapped content that wrapper collapses to 0x0, so the badge ends
      // up positioned arbitrarily instead of centered next to the label.
      // Force `inline` (V3's actual "badge in normal flow" mode) whenever
      // there's nothing to overlap onto.
      const inline = normalizedAttrs.inline ?? !slots.default

      return h(
        VBadge,
        {
          ...normalizedAttrs,
          inline,
          ...(content !== undefined ? { content } : {}),
          ...(modelValue !== undefined ? { modelValue: visible } : {})
        },
        slots
      )
    }
  }
})
